import { useCallback, useEffect, useState } from "react";
import { http, API } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function usePushNotifications() {
  const supported = typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;

  const [permission, setPermission] = useState(supported ? Notification.permission : "denied");
  const [subscription, setSubscription] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscription(sub);
    } catch (_e) {}
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = useCallback(async () => {
    if (!supported) return { ok: false, error: "Push not supported on this browser" };
    setBusy(true);
    try {
      let p = Notification.permission;
      if (p === "default") p = await Notification.requestPermission();
      setPermission(p);
      if (p !== "granted") return { ok: false, error: "Permission " + p };

      const reg = await navigator.serviceWorker.ready;
      const { data } = await http.get("/push/public-key");
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      const json = sub.toJSON();
      await http.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        device_label: navigator.userAgent.slice(0, 80),
      });
      setSubscription(sub);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message || String(e) };
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported || !subscription) return { ok: true };
    setBusy(true);
    try {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe().catch(() => {});
      await http.post("/push/unsubscribe", { endpoint });
      setSubscription(null);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    } finally {
      setBusy(false);
    }
  }, [supported, subscription]);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await http.post("/push/test", { title: "Load Logic", body: "Push notifications wired up." });
      return { ok: true, delivered: data.delivered };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  }, []);

  return { supported, permission, subscription, busy, enable, disable, sendTest, refresh };
}
